import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  CLASES_REFRACCION, FARMACOS_INTRAVITREOS, FORMULAS, METODOS_PIO, OJOS, TECNICAS_REFRACTIVA, TIPOS_LENTE,
  ablacionMunnerlyn, dpt, ee, num, seguridadLaser,
} from '../../lib/oftalmo';

const sel = (opciones) => ({ tipo: 'select', opciones });

/** Qué se pide de cada tipo de registro. `ojo`: campos por ojo (OD | OI);
 *  `campos`: los generales. Lo mismo que guarda el servidor
 *  (routers/oftalmologia_router.TIPOS). */
export const FORMULARIOS = {
  pio: {
    titulo: 'Tensión ocular (PIO)',
    campos: [['metodo', 'Método', sel(METODOS_PIO)], ['hora', 'Hora', { tipo: 'time' }],
      ['tratamiento', 'Tratamiento en ese momento'], ['notas', 'Notas']],
    ojo: [['valor', 'PIO (mmHg)'], ['paquimetria', 'Paquimetría (µm)']],
  },
  refraccion: {
    titulo: 'Refracción',
    campos: [['clase', 'Tipo', sel(Object.keys(CLASES_REFRACCION)), CLASES_REFRACCION], ['notas', 'Notas']],
    ojo: [['esf', 'Esfera (D)'], ['cil', 'Cilindro (D)'], ['eje', 'Eje (°)'], ['add', 'Adición (D)'],
      ['av_sc', 'AV sin corrección (dec.)'], ['av_cc', 'AV con corrección (dec.)']],
  },
  plan_refractivo: {
    titulo: 'Plan de cirugía refractiva',
    campos: [['tecnica', 'Técnica', sel(TECNICAS_REFRACTIVA)], ['laser', 'Láser / lente'],
      ['fecha_cirugia', 'Fecha de cirugía', { tipo: 'date' }], ['cirujano', 'Cirujano'], ['notas', 'Notas']],
    ojo: [['esf', 'Esfera a tratar (D)'], ['cil', 'Cilindro (D)'], ['eje', 'Eje (°)'],
      ['objetivo_ee', 'Objetivo EE (D)'], ['zona_optica', 'Zona óptica (mm)'], ['paquimetria', 'Paquimetría (µm)'],
      ['colgajo', 'Colgajo / cap (µm; 0 en PRK)'], ['ablacion_um', 'Ablación del láser (µm, si la da)']],
  },
  biometria: {
    titulo: 'Biometría',
    campos: [['equipo', 'Biómetro'], ['notas', 'Notas']],
    ojo: [['al', 'Longitud axial (mm)'], ['k1', 'K1 (D)'], ['k2', 'K2 (D)'], ['k1_eje', 'Eje K1 (°)'],
      ['acd', 'ACD (mm)'], ['lt', 'Grosor cristalino (mm)'], ['wtw', 'Blanco-blanco (mm)'], ['cct', 'Paquimetría (µm)']],
  },
  lio: {
    titulo: 'Lente intraocular',
    campos: [['ojo', 'Ojo', sel(['OD', 'OI'])], ['fase', 'Fase', sel(['planificada', 'implantada']),
      { planificada: 'Planificada (cálculo)', implantada: 'Implantada (cirugía)' }],
    ['modelo', 'Modelo'], ['tipo_lente', 'Tipo', sel(TIPOS_LENTE)], ['potencia', 'Potencia (D)', { tipo: 'num' }],
    ['cilindro', 'Cilindro LIO (D)', { tipo: 'num' }], ['eje', 'Eje de implante (°)', { tipo: 'num' }],
    ['formula', 'Fórmula', sel(FORMULAS)], ['constante_a', 'Constante A', { tipo: 'num' }],
    ['refraccion_esperada', 'Refracción esperada (EE, D)', { tipo: 'num' }],
    ['fecha_cirugia', 'Fecha de cirugía', { tipo: 'date' }], ['cirujano', 'Cirujano'],
    ['posicion', 'Posición', sel(['Saco capsular', 'Sulcus', 'Fijación escleral', 'Cámara anterior'])],
    ['lote', 'Nº de serie / lote'], ['incidencias', 'Incidencias'], ['notas', 'Notas']],
    ojo: [],
  },
  inyeccion: {
    titulo: 'Inyección intravítrea',
    campos: [['ojo', 'Ojo', sel(['OD', 'OI'])], ['farmaco', 'Fármaco', sel(FARMACOS_INTRAVITREOS)],
      ['indicacion', 'Indicación', sel(['DMAE húmeda', 'Edema macular diabético', 'Oclusión venosa', 'Miopía patológica',
        'Atrofia geográfica', 'Otra'])], ['lote', 'Lote'], ['notas', 'Notas']],
    ojo: [],
  },
  campo_visual: {
    titulo: 'Campo visual',
    campos: [['estrategia', 'Estrategia', sel(['24-2 SITA Standard', '24-2 SITA Fast', '10-2', '30-2', 'Esterman'])],
      ['fiabilidad', 'Fiabilidad', sel(['Fiable', 'Poco fiable'])], ['notas', 'Notas']],
    ojo: [['md', 'MD (dB)'], ['psd', 'PSD (dB)'], ['vfi', 'VFI (%)']],
  },
  topografia: {
    titulo: 'Topografía (sin imagen)',
    campos: [['equipo', 'Topógrafo / tomógrafo'], ['notas', 'Valoración']],
    ojo: [['kmax', 'Kmax (D)'], ['k1', 'K1 (D)'], ['k2', 'K2 (D)'], ['astig', 'Astig. corneal (D)'], ['paqui_min', 'Paquimetría mínima (µm)']],
  },
  oct: {
    titulo: 'Medidas de OCT (sin imagen)',
    campos: [['equipo', 'Equipo'], ['notas', 'Notas']],
    ojo: [['cst', 'Grosor central (µm)'], ['volumen', 'Volumen (mm³)'], ['cfnr', 'CFNR medio (µm)']],
  },
};

function vacio(tipo, inicial) {
  const f = FORMULARIOS[tipo];
  const v = { fecha: new Date().toISOString().slice(0, 10) };
  f.campos.forEach(([k, , o]) => { v[k] = o?.tipo === 'select' ? o.opciones[0] : ''; });
  if (tipo === 'refraccion') v.clase = 'subjetiva';
  if (tipo === 'lio') v.fase = 'implantada';
  OJOS.forEach(([o]) => { v[o] = {}; });
  return { ...v, ...(inicial || {}) };
}

function Campo({ def, valor, onChange }) {
  const [k, etiqueta, opc, nombres] = def;
  const id = `reg-${k}`;
  if (opc?.tipo === 'select') {
    return (
      <label className="text-xs text-muted-foreground flex flex-col gap-1" htmlFor={id}>
        {etiqueta}
        <select id={id} value={valor ?? ''} onChange={(e) => onChange(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
          {opc.opciones.map((o) => <option key={o} value={o}>{nombres?.[o] || o}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className="text-xs text-muted-foreground flex flex-col gap-1" htmlFor={id}>
      {etiqueta}
      <Input id={id} type={opc?.tipo === 'date' ? 'date' : opc?.tipo === 'time' ? 'time' : 'text'}
        inputMode={opc?.tipo === 'num' ? 'decimal' : undefined}
        value={valor ?? ''} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm text-foreground" />
    </label>
  );
}

/** Alta o edición de un registro. `registro` = el que se edita (o null). */
export default function RegistroDialog({ open, onOpenChange, pid, tipo, registro, inicial, onGuardado }) {
  const def = FORMULARIOS[tipo] || FORMULARIOS.pio;
  const [v, setV] = useState(() => vacio(tipo, registro || inicial));
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { if (open) setV(vacio(tipo, registro || inicial)); }, [open, tipo, registro, inicial]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const setOjo = (o, k, val) => setV((p) => ({ ...p, [o]: { ...(p[o] || {}), [k]: val } }));

  // Lo calculado, al vuelo (lo mismo calcula el servidor al guardar).
  const calculo = useMemo(() => {
    const out = {};
    OJOS.forEach(([o]) => {
      const d = v[o] || {};
      if (tipo === 'refraccion') out[o] = { ee: ee(d.esf, d.cil) };
      if (tipo === 'plan_refractivo') {
        const abl = num(d.ablacion_um) ?? ablacionMunnerlyn(d.esf, d.cil, d.zona_optica);
        out[o] = { ee: ee(d.esf, d.cil), ablacion: abl, ...seguridadLaser(d.paquimetria, d.colgajo, abl) };
      }
      if (tipo === 'biometria' && num(d.k1) !== null && num(d.k2) !== null) {
        out[o] = { k: ((num(d.k1) + num(d.k2)) / 2).toFixed(2), astig: Math.abs(num(d.k2) - num(d.k1)).toFixed(2) };
      }
    });
    return out;
  }, [v, tipo]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = { ...v, tipo };
      const r = registro
        ? await api.put(`/api/patients/${pid}/oftalmologia/registros/${registro.id}`, payload)
        : await api.post(`/api/patients/${pid}/oftalmologia/registros`, payload);
      toast.success(registro ? 'Registro actualizado' : 'Registro guardado');
      onGuardado?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={`oft-dialog-${tipo}`}>
        <DialogHeader><DialogTitle>{registro ? 'Editar' : 'Nuevo'}: {def.titulo}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Campo def={['fecha', 'Fecha', { tipo: 'date' }]} valor={v.fecha} onChange={(x) => set('fecha', x)} />
          {def.campos.map((c) => <Campo key={c[0]} def={c} valor={v[c[0]]} onChange={(x) => set(c[0], x)} />)}
        </div>
        {def.ojo.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {OJOS.map(([o, OJO]) => (
              <div key={o} className="rounded-lg border p-3" style={{ borderColor: OJO === 'OD' ? '#FECACA' : '#BFDBFE' }}>
                <div className="font-semibold text-sm mb-2" style={{ color: OJO === 'OD' ? '#B91C1C' : '#1D4ED8' }}>
                  {OJO === 'OD' ? 'Ojo derecho (OD)' : 'Ojo izquierdo (OI)'}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {def.ojo.map(([k, etiqueta]) => (
                    <label key={k} className="text-xs text-muted-foreground flex flex-col gap-1">
                      {etiqueta}
                      <Input inputMode="decimal" value={v[o]?.[k] ?? ''} data-testid={`oft-${o}-${k}`}
                        onChange={(e) => setOjo(o, k, e.target.value)} className="h-9 text-sm text-foreground" />
                    </label>
                  ))}
                </div>
                {calculo[o] && (
                  <div className="mt-2 text-xs space-y-0.5 text-[#13231E] dark:text-foreground">
                    {calculo[o].ee !== undefined && calculo[o].ee !== null && <div>Equivalente esférico: <b>{dpt(calculo[o].ee)}</b></div>}
                    {calculo[o].k && <div>K media <b>{calculo[o].k} D</b> · astigmatismo corneal <b>{calculo[o].astig} D</b></div>}
                    {tipo === 'plan_refractivo' && (
                      <>
                        <div>Ablación: <b>{calculo[o].ablacion ?? '—'} µm</b>{num(v[o]?.ablacion_um) === null && calculo[o].ablacion ? ' (Munnerlyn)' : ''}</div>
                        <div>Lecho residual: <b>{calculo[o].lecho ?? '—'} µm</b> · PTA <b>{calculo[o].pta ?? '—'} %</b></div>
                        {calculo[o].avisos.map((a) => (
                          <div key={a} className="flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3" />{a}</div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando} data-testid="oft-guardar-registro">
            {guardando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
